/**
 * module-ui-catalog — katalog widget-first modulových UI.
 *
 * 1) Modulová stránka (/core/containers, /modules/gitlab, …) bere záložky odtud.
 * 2) Node workspace (/infra/nodes/:nodeId) dynamicky nabídne stejné záložky
 *    pro každý modul, který cílový nod má nainstalovaný.
 *
 * Přidání nového modulu do widget UI:
 *   - widgety do registry.<modul>.tsx
 *   - panely do module-panels.ts
 *   - jeden záznam sem (tabs → panel refs)
 *   - (volitelně) tenká page wrapper přes ModuleWorkspace
 */
import type { LucideIcon } from 'lucide-react';
import { Bell,
  Activity,
  Archive,
  BookOpen,
  BookUser,
  Box,
  Cpu,
  Database,
  Download,
  Eye,
  FileText,
  GitCompare,
  GitMerge,
  HardDrive,
  Image as ImageIcon,
  Key,
  KeyRound,
  Monitor,
  Layers,
  Network,
  Package,
  Plus,
  Puzzle,
  RefreshCw,
  Route,
  ScrollText,
  Server,
  Globe,
  Settings,
  Shield,
  ShieldCheck,
  Trash2,
  Upload,
  User,
  Users,
  Wand2,
  Wifi,
  Wrench,
  CalendarClock,
  FolderKanban,
  History,
  Home,
  Lock,
  Signpost,
} from 'lucide-react';
import { CodableIcon } from '../components/icons/CodableIcon';
import type { WidgetPanel } from './types';
import {
  DOCKER_CONTAINERS,
  DOCKER_IMAGES,
  DOCKER_NETWORKS,
  DOCKER_VOLUMES,
  GITLAB_MAIN,
  GITLAB_MAINTENANCE,
  GITLAB_WORKERS,
  APPHOST_INSTANCES,
  APPHOST_LOVABLE,
  NPM_CERTS,
  NPM_EDGE,
  NPM_HOSTS,
  NPM_SETTINGS,
  NPM_STREAM,
  NPM_TEMPLATES,
  NPM_TTY,
  SUPABASE_INSTANCES,
  SUPABASE_SCHEMATA,
  SUPABASE_USERS,
  TOOLBOX_ACM_LINK,
  TOOLBOX_BACKUP,
  TOOLBOX_CLEANER,
  TOOLBOX_CRASH_RECOVERY,
  TOOLBOX_HEALTH,
  TOOLBOX_RESOURCES_MGMT,
  TOOLBOX_SECURITY,
  TOOLBOX_SYSTEM,
  ORCHESTRATOR_OVERVIEW,
  TOOLBOX_SYSTEM_MGMT,
  TOOLBOX_UPDATES,
  TOOLBOX_WATCHDOG,
  NOTIFICATIONS_HISTORY,
  CODABLE_PROJECTS,
  CODABLE_LIBRARY,
  CODABLE_SETTINGS,
  DNS_PROVIDERS,
  DNS_ZONES,
  DNS_TEMPLATES,
  DNS_SERVER,
  DNS_DOMAINS,
  NETWORK_OVERVIEW,
  REGISTRY_TREE,
  REGISTRY_HEALTH,
  REGISTRY_CATALOG,
  REGISTRY_PUBLISH,
  CERTMGR_OVERVIEW,
  CERTMGR_CERTIFICATES,
  CERTMGR_STORE,
  CERTMGR_TRUST,
  CERTMGR_LE,
  CERTMGR_DNS,
  CERTMGR_DOCKER,
  ACM_SELF_SERVICES,
  ACM_SELF_ACL,
  ACM_SELF_DOMAINS,
  ACM_SELF_SECURITY,
  TASKS_USER,
  TASKS_SYSTEM,
  TASKS_MODULES,
  TASKS_PROJECTS,
  TASKS_RUNS,
  TASKS_EVENTS,
  MESH_FLEET,
  MESH_RESOURCES,
  MESH_HEALTH,
  MESH_ROUTING,
  MESH_ENROLLMENT,
  MESH_DISCOVERED,
  MESH_BOOTSTRAP,
  MESH_SECURITY,
  MESH_TENANTS,
  MESH_TRUST,
  RUSTDESK_STATIONS,
  RUSTDESK_BOOKS,
  RUSTDESK_PACKAGES,
  RUSTDESK_AUDIT,
  RUSTDESK_SERVER,
  RUSTDESK_CONTAINERS,
  UTM_OVERVIEW,
  UTM_NAT,
  UTM_RULES,
  UTM_ALIASES,
  UTM_ROUTES,
  UTM_INTERFACES,
  UTM_NET,
  UTM_INSTANCE,
  ADDONSTORE_BROWSE,
  ADDONSTORE_INSTALLED,
  ADDONSTORE_AVAILABLE,
  ADDONSTORE_HA,
} from './module-panels';

/** Jedna záložka = jeden ModulePanel (zamčený default layout). */
export interface ModuleUiTab {
  id: string;
  label: string;
  /** i18n key (volitelné) */
  labelKey?: string;
  icon?: LucideIcon;
  panel: WidgetPanel;
}

/**
 * Definice modulového UI v katalogu.
 * `module` = jméno v module.json / useNodeModules (availability na nodu).
 * `null` = vždy dostupné (core glance, nezávisí na instalaci modulu).
 */
export interface ModuleUiDef {
  /** ACM module name, nebo null = vždy na Node workspace */
  module: string | null;
  /** Stabilní id pro route / tab grouping */
  id: string;
  title: string;
  titleKey?: string;
  description?: string;
  descriptionKey?: string;
  icon?: LucideIcon;
  tabs: ModuleUiTab[];
  /**
   * Pokud true, na Node workspace se zobrazí celý balík tabů.
   * false = jen modulová stránka (zatím ne na infrastruktuře).
   */
  showOnNode?: boolean;
  /**
   * Skupina v Node workspace sidebaru (host / workloads / data / platform).
   * Jen pro showOnNode — flat TabList je nepoužitelný při 50+ záložkách.
   */
  nodeGroup?: NodeWorkspaceGroup;
}

/** Skupiny navigace Node workspace. */
export type NodeWorkspaceGroup = 'host' | 'workloads' | 'data' | 'platform';

/**
 * Katalog — pořadí = pořadí záložek na Node workspace (po Overview).
 * Docker/GitLab/System už mají plné widget panely = první vlna.
 */
export const MODULE_UI_CATALOG: ModuleUiDef[] = [
  {
    module: null,
    id: 'system-glance',
    nodeGroup: 'host',
    title: 'System',
    titleKey: 'infra.tabSystem',
    description: 'Host glance (CPU/RAM/disk)',
    descriptionKey: 'infra.systemGlanceDesc',
    icon: Server,
    showOnNode: true,
    tabs: [
      {
        id: 'system',
        label: 'System',
        labelKey: 'infra.tabSystem',
        icon: Server,
        panel: TOOLBOX_SYSTEM,
      },
    ],
  },
  {
    module: 'docker',
    id: 'docker',
    nodeGroup: 'workloads',
    title: 'Containers',
    titleKey: 'containers.pageTitle',
    description: 'Docker — containers, images, volumes, networks',
    descriptionKey: 'containers.pageSubtitle',
    icon: Box,
    showOnNode: true,
    tabs: [
      {
        id: 'containers',
        label: 'Containers',
        labelKey: 'containers.tabContainers',
        icon: Box,
        panel: DOCKER_CONTAINERS,
      },
      {
        id: 'images',
        label: 'Images',
        labelKey: 'containers.tabImages',
        icon: ImageIcon,
        panel: DOCKER_IMAGES,
      },
      {
        id: 'volumes',
        label: 'Volumes',
        labelKey: 'containers.tabVolumes',
        icon: HardDrive,
        panel: DOCKER_VOLUMES,
      },
      {
        id: 'networks',
        label: 'Networks',
        labelKey: 'containers.tabNetworks',
        icon: Network,
        panel: DOCKER_NETWORKS,
      },
    ],
  },
  {
    module: 'dns',
    id: 'dns',
    nodeGroup: 'platform',
    title: 'DNS',
    titleKey: 'dns.pageTitle',
    description: 'Providers, zones (live/import/export/versions), templates, server, domains',
    descriptionKey: 'dns.pageDescription',
    icon: Signpost,
    showOnNode: true,
    tabs: [
      {
        id: 'zones',
        label: 'Zones',
        labelKey: 'dns.tabZones',
        icon: Globe,
        panel: DNS_ZONES,
      },
      {
        id: 'providers',
        label: 'Providers',
        labelKey: 'dns.tabProviders',
        icon: KeyRound,
        panel: DNS_PROVIDERS,
      },
      {
        id: 'templates',
        label: 'Templates',
        labelKey: 'dns.tabTemplates',
        icon: Wand2,
        panel: DNS_TEMPLATES,
      },
      {
        id: 'server',
        label: 'Server',
        labelKey: 'dns.tabServer',
        icon: Server,
        panel: DNS_SERVER,
      },
      {
        id: 'domains',
        label: 'Domains',
        labelKey: 'dns.tabDomains',
        icon: Shield,
        panel: DNS_DOMAINS,
      },
    ],
  },
  {
    module: 'utm',
    id: 'utm',
    nodeGroup: 'host',
    title: 'UTM',
    titleKey: 'utm.pageTitle',
    description: 'OPNsense show/set/discard/commit — nat, rule, alias, route, net, instance',
    descriptionKey: 'utm.pageDescription',
    icon: Shield,
    showOnNode: true,
    tabs: [
      {
        id: 'overview',
        label: 'Compare',
        icon: GitCompare,
        panel: UTM_OVERVIEW,
      },
      {
        id: 'nat',
        label: 'NAT',
        icon: Route,
        panel: UTM_NAT,
      },
      {
        id: 'rules',
        label: 'Rules',
        icon: Shield,
        panel: UTM_RULES,
      },
      {
        id: 'aliases',
        label: 'Aliases',
        icon: Signpost,
        panel: UTM_ALIASES,
      },
      {
        id: 'routes',
        label: 'Routes',
        icon: Layers,
        panel: UTM_ROUTES,
      },
      {
        id: 'interfaces',
        label: 'Interfaces',
        icon: Network,
        panel: UTM_INTERFACES,
      },
      {
        id: 'net',
        label: 'Net',
        icon: User,
        panel: UTM_NET,
      },
      {
        id: 'instance',
        label: 'Instance',
        icon: Server,
        panel: UTM_INSTANCE,
      },
    ],
  },
  {
    module: 'codable',
    id: 'codable',
    nodeGroup: 'workloads',
    title: 'Codable',
    titleKey: 'codable.title',
    description: 'Projects, workspace, settings — node-aware',
    descriptionKey: 'codable.description',
    icon: CodableIcon,
    showOnNode: true,
    tabs: [
      {
        id: 'projects',
        label: 'Projects',
        labelKey: 'codable.tabProjects',
        icon: FolderKanban,
        panel: CODABLE_PROJECTS,
      },
      {
        id: 'library',
        label: 'Library',
        labelKey: 'codable.tabLibrary',
        icon: BookOpen,
        panel: CODABLE_LIBRARY,
      },
      {
        id: 'settings',
        label: 'Settings',
        labelKey: 'codableSettings.gearLabel',
        icon: Settings,
        panel: CODABLE_SETTINGS,
      },
    ],
  },
  {
    module: 'orchestrator',
    id: 'orchestrator',
    nodeGroup: 'workloads',
    title: 'Orchestrator',
    titleKey: 'orchestrator.pageTitle',
    description: 'Environments, services, release pipeline',
    descriptionKey: 'orchestrator.pageDescription',
    icon: GitMerge,
    showOnNode: true,
    tabs: [
      {
        id: 'overview',
        label: 'Overview',
        labelKey: 'phone.orch.tabOverview',
        icon: Activity,
        panel: ORCHESTRATOR_OVERVIEW,
      },
    ],
  },
  {
    module: 'gitlab',
    id: 'gitlab',
    nodeGroup: 'platform',
    title: 'GitLab',
    titleKey: 'gitlab.pageTitle',
    description: 'Instances, projects, servers, workers — GitLab and GitHub',
    descriptionKey: 'gitlab.pageDescription',
    icon: GitMerge,
    showOnNode: true,
    tabs: [
      {
        id: 'gitlab',
        label: 'GitLab',
        labelKey: 'gitlab.tabs.gitlab',
        icon: GitMerge,
        panel: GITLAB_MAIN,
      },
      {
        id: 'workers',
        label: 'Workers',
        labelKey: 'gitlab.tabs.workers',
        icon: Cpu,
        panel: GITLAB_WORKERS,
      },
      {
        id: 'maintenance',
        label: 'Údržba',
        labelKey: 'gitlab.tabs.maintenance',
        icon: Wrench,
        panel: GITLAB_MAINTENANCE,
      },
    ],
  },
  {
    module: 'toolbox',
    id: 'toolbox',
    nodeGroup: 'host',
    title: 'Toolbox',
    titleKey: 'toolbox.pageTitle',
    description: 'Health, security, updates, backup, system tools',
    descriptionKey: 'toolbox.healthSecurityUpdatesBackup',
    icon: Wrench,
    showOnNode: true,
    tabs: [
      {
        id: 'health',
        label: 'Health',
        labelKey: 'toolbox.tabHealth',
        icon: Activity,
        panel: TOOLBOX_HEALTH,
      },
      {
        id: 'security',
        label: 'Security',
        labelKey: 'toolbox.tabSecurity',
        icon: Shield,
        panel: TOOLBOX_SECURITY,
      },
      {
        id: 'updates',
        label: 'Updates',
        labelKey: 'toolbox.tabUpdates',
        icon: RefreshCw,
        panel: TOOLBOX_UPDATES,
      },
      {
        id: 'backup',
        label: 'Backup',
        labelKey: 'toolbox.tabBackup',
        icon: Archive,
        panel: TOOLBOX_BACKUP,
      },
      {
        id: 'cleaner',
        label: 'Cleaner',
        labelKey: 'toolbox.tabCleaner',
        icon: Trash2,
        panel: TOOLBOX_CLEANER,
      },
      {
        id: 'watchdog',
        label: 'Watchdog',
        labelKey: 'toolbox.tabWatchdog',
        icon: Eye,
        panel: TOOLBOX_WATCHDOG,
      },
      {
        id: 'crash-recovery',
        label: 'Crash recovery',
        labelKey: 'toolbox.uiCrashRecovery',
        icon: Shield,
        panel: TOOLBOX_CRASH_RECOVERY,
      },
      {
        id: 'resources',
        label: 'Resources',
        labelKey: 'toolbox.tabResources',
        icon: HardDrive,
        panel: TOOLBOX_RESOURCES_MGMT,
      },
      {
        id: 'system',
        label: 'System',
        labelKey: 'toolbox.tabSystem',
        icon: Settings,
        panel: TOOLBOX_SYSTEM_MGMT,
      },
      {
        id: 'acm-link',
        label: 'ACMLink',
        labelKey: 'toolbox.tabAcmLink',
        icon: Puzzle,
        panel: TOOLBOX_ACM_LINK,
      },
    ],
  },
  {
    module: 'supabase',
    id: 'supabase',
    nodeGroup: 'data',
    title: 'Supabase',
    titleKey: 'supabase.pageTitle',
    description: 'Instance, schemata, ACL — widget-first',
    descriptionKey: 'supabase.pageDescription',
    icon: Database,
    showOnNode: true,
    tabs: [
      {
        id: 'instances',
        label: 'Instance',
        labelKey: 'supabase.tabInstances',
        icon: Database,
        panel: SUPABASE_INSTANCES,
      },
      {
        id: 'schemata',
        label: 'Schemata',
        labelKey: 'supabase.tabSchemata',
        icon: Layers,
        panel: SUPABASE_SCHEMATA,
      },
      {
        id: 'users',
        label: 'Users',
        labelKey: 'supabase.tabUsers',
        icon: Users,
        panel: SUPABASE_USERS,
      },
    ],
  },
  {
    module: 'apphost',
    id: 'apphost',
    nodeGroup: 'workloads',
    title: 'AppHost',
    titleKey: 'apphost.pageTitle',
    description: 'Laravel / React / Node apps — deploy, config, Lovable',
    descriptionKey: 'apphost.pageDescription',
    icon: Server,
    showOnNode: true,
    tabs: [
      {
        id: 'instances',
        label: 'Instances',
        labelKey: 'apphost.instances',
        icon: Server,
        panel: APPHOST_INSTANCES,
      },
      {
        id: 'lovable',
        label: 'Lovable',
        labelKey: 'apphost.tabLovable',
        icon: Wand2,
        panel: APPHOST_LOVABLE,
      },
    ],
  },
  {
    module: 'npm',
    id: 'npm',
    nodeGroup: 'workloads',
    title: 'Proxy',
    titleKey: 'npm.proxyManager',
    description: 'NGINX Proxy Manager — hosts, stream, certs, edge',
    descriptionKey: 'npm.httpReverseProxyTcp',
    icon: Globe,
    showOnNode: true,
    tabs: [
      {
        id: 'hosts',
        label: 'HTTP hosts',
        labelKey: 'npm.httpHosty',
        icon: Globe,
        panel: NPM_HOSTS,
      },
      {
        id: 'stream',
        label: 'Stream',
        labelKey: 'npm.tabStream',
        icon: Server,
        panel: NPM_STREAM,
      },
      {
        id: 'tty',
        label: 'TTY',
        labelKey: 'npm.tabTty',
        icon: Wifi,
        panel: NPM_TTY,
      },
      {
        id: 'certs',
        label: 'Certs',
        labelKey: 'npm.certifikaty',
        icon: Shield,
        panel: NPM_CERTS,
      },
      {
        id: 'templates',
        label: 'Templates',
        labelKey: 'npm.sablony',
        icon: Layers,
        panel: NPM_TEMPLATES,
      },
      {
        id: 'edge',
        label: 'Edge',
        labelKey: 'npm.edgeProxy',
        icon: Globe,
        panel: NPM_EDGE,
      },
      {
        id: 'settings',
        label: 'Settings',
        labelKey: 'npm.nastaveni',
        icon: Settings,
        panel: NPM_SETTINGS,
      },
    ],
  },
  {
    module: 'acm-self',
    id: 'acm-self',
    nodeGroup: 'host',
    title: 'Public access',
    titleKey: 'acmSelf.pageTitle',
    description: 'Public access to ACM control-plane services only',
    descriptionKey: 'acmSelf.pageDescription',
    icon: Globe,
    showOnNode: true,
    tabs: [
      {
        id: 'services',
        label: 'Services',
        labelKey: 'acmSelf.tabServices',
        icon: Globe,
        panel: ACM_SELF_SERVICES,
      },
      {
        id: 'acl',
        label: 'Admin ACL',
        labelKey: 'acmSelf.tabAcl',
        icon: KeyRound,
        panel: ACM_SELF_ACL,
      },
      {
        id: 'domains',
        label: 'Domains',
        labelKey: 'acmSelf.tabDomains',
        icon: Globe,
        panel: ACM_SELF_DOMAINS,
      },
      {
        id: 'security',
        label: 'Security',
        labelKey: 'acmSelf.tabSecurity',
        icon: Key,
        panel: ACM_SELF_SECURITY,
      },
    ],
  },
  {
    module: 'network',
    id: 'network',
    nodeGroup: 'host',
    title: 'Network',
    titleKey: 'network.pageTitle',
    description: 'Published public URLs across the platform',
    descriptionKey: 'network.subtitle',
    icon: Globe,
    showOnNode: true,
    tabs: [
      {
        id: 'overview',
        label: 'Public URLs',
        labelKey: 'network.publishedUrls',
        icon: Globe,
        panel: NETWORK_OVERVIEW,
      },
    ],
  },
  {
    module: 'registry',
    id: 'registry',
    nodeGroup: 'data',
    title: 'Registry',
    titleKey: 'registry.pageTitle',
    description: 'Registry of functions and nodes',
    descriptionKey: 'registry.pageDescription',
    icon: Server,
    showOnNode: true,
    tabs: [
      {
        id: 'tree',
        label: 'Resource Tree',
        labelKey: 'registry.tabResourceTree',
        icon: Server,
        panel: REGISTRY_TREE,
      },
      {
        id: 'health',
        label: 'Health',
        labelKey: 'registry.tabHealth',
        icon: RefreshCw,
        panel: REGISTRY_HEALTH,
      },
    ],
  },
  {
    // Core stránka /core/store — module null = vždy na Node workspace
    // (instalace balíčku na jiném nodu).
    module: null,
    id: 'addonstore',
    nodeGroup: 'platform',
    title: 'Moduly',
    titleKey: 'nav.modules',
    description: 'Instalace, správa a aktualizace modulů',
    descriptionKey: 'addonStore.pageDescription',
    icon: Package,
    showOnNode: true,
    tabs: [
      {
        id: 'browse',
        label: 'Overview',
        labelKey: 'addonStore.tabBrowse',
        icon: Layers,
        panel: ADDONSTORE_BROWSE,
      },
      {
        id: 'installed',
        label: 'Installed',
        labelKey: 'addonStore.tabInstalled',
        icon: Box,
        panel: ADDONSTORE_INSTALLED,
      },
      {
        id: 'available',
        label: 'Available',
        labelKey: 'addonStore.tabAvailable',
        icon: Globe,
        panel: ADDONSTORE_AVAILABLE,
      },
      {
        id: 'catalog',
        label: 'Catalog',
        labelKey: 'addonStore.tabCatalog',
        icon: Package,
        panel: REGISTRY_CATALOG,
      },
      {
        id: 'publish',
        label: 'Publish',
        labelKey: 'addonStore.tabPublish',
        icon: Upload,
        panel: REGISTRY_PUBLISH,
      },
      {
        id: 'ha',
        label: 'HA Addons',
        labelKey: 'addonStore.haAddonsTab',
        icon: Home,
        panel: ADDONSTORE_HA,
      },
    ],
  },
  {
    module: 'certmgr',
    id: 'certmgr',
    nodeGroup: 'platform',
    title: 'Certificates',
    titleKey: 'certmgr.pageTitle',
    description: 'Root CA, mesh certs, LE, trust anchors',
    descriptionKey: 'certmgr.pageDescription',
    icon: Shield,
    showOnNode: true,
    tabs: [
      {
        id: 'overview',
        label: 'Overview',
        labelKey: 'certmgr.prehled',
        icon: Shield,
        panel: CERTMGR_OVERVIEW,
      },
      {
        id: 'certificates',
        label: 'Mesh certificates',
        labelKey: 'certmgr.meshCertifikaty',
        icon: FileText,
        panel: CERTMGR_CERTIFICATES,
      },
      {
        id: 'store',
        label: 'Cert Store',
        labelKey: 'certmgr.certStore',
        icon: FileText,
        panel: CERTMGR_STORE,
      },
      {
        id: 'trust',
        label: 'Trust Anchors',
        labelKey: 'certmgr.trustAnchors',
        icon: Download,
        panel: CERTMGR_TRUST,
      },
      {
        id: 'le',
        label: "Let's Encrypt",
        labelKey: 'certmgr.letSEncrypt',
        icon: Globe,
        panel: CERTMGR_LE,
      },
      {
        id: 'dns',
        label: 'DNS Providers',
        labelKey: 'certmgr.dnsProviders',
        icon: Key,
        panel: CERTMGR_DNS,
      },
      {
        id: 'docker',
        label: 'Root CA',
        labelKey: 'certmgr.rootCaDocker',
        icon: Server,
        panel: CERTMGR_DOCKER,
      },
    ],
  },
  {
    module: 'mesh',
    id: 'mesh',
    title: 'Infrastructure',
    titleKey: 'mesh.pageTitle',
    description: 'Mesh nodes, topology, tenants, enrollment — widget panels',
    descriptionKey: 'mesh.pageDescription',
    icon: Network,
    showOnNode: false,
    tabs: [
      {
        id: 'infra',
        label: 'Nodes',
        labelKey: 'mesh.tabInfra',
        icon: Network,
        panel: MESH_FLEET,
      },
      {
        id: 'resources',
        label: 'Registry',
        labelKey: 'mesh.tabResources',
        icon: Database,
        panel: MESH_RESOURCES,
      },
      {
        id: 'health',
        label: 'Health',
        labelKey: 'mesh.tabHealth',
        icon: Activity,
        panel: MESH_HEALTH,
      },
      {
        id: 'routing',
        label: 'Routing & quorum',
        labelKey: 'mesh.tabRouting',
        icon: Signpost,
        panel: MESH_ROUTING,
      },
      {
        id: 'enrollment',
        label: 'Add node',
        labelKey: 'mesh.tabEnrollment',
        icon: Plus,
        panel: MESH_ENROLLMENT,
      },
      {
        id: 'discovered',
        label: 'Discovered',
        labelKey: 'mesh.tabDiscovered',
        icon: Wifi,
        panel: MESH_DISCOVERED,
      },
      {
        id: 'bootstrap',
        label: 'Bootstrap',
        labelKey: 'mesh.tabBootstrap',
        icon: Key,
        panel: MESH_BOOTSTRAP,
      },
      {
        id: 'security',
        label: 'Security',
        labelKey: 'mesh.tabSecurity',
        icon: Shield,
        panel: MESH_SECURITY,
      },
      {
        id: 'trust',
        label: 'Trust',
        labelKey: 'mesh.tabTrust',
        icon: ShieldCheck,
        panel: MESH_TRUST,
      },
      {
        id: 'tenants',
        label: 'Tenants',
        labelKey: 'mesh.tabTenants',
        icon: Users,
        panel: MESH_TENANTS,
      },
    ],
  },
  {
    module: 'tasks',
    id: 'tasks',
    nodeGroup: 'platform',
    title: 'Task Manager',
    titleKey: 'tasks.pageTitle',
    description: 'Task scheduler — mine, system, modules and projects',
    descriptionKey: 'tasks.pageDescription',
    icon: CalendarClock,
    showOnNode: true,
    tabs: [
      {
        id: 'user',
        label: 'Mine',
        labelKey: 'tasks.tabUser',
        icon: User,
        panel: TASKS_USER,
      },
      {
        id: 'system',
        label: 'System',
        labelKey: 'tasks.tabSystem',
        icon: Lock,
        panel: TASKS_SYSTEM,
      },
      {
        id: 'modules',
        label: 'Modules',
        labelKey: 'tasks.tabModules',
        icon: Package,
        panel: TASKS_MODULES,
      },
      {
        id: 'projects',
        label: 'Projects',
        labelKey: 'tasks.tabProjects',
        icon: FolderKanban,
        panel: TASKS_PROJECTS,
      },
      {
        id: 'runs',
        label: 'Runs',
        labelKey: 'tasks.tabRuns',
        icon: History,
        panel: TASKS_RUNS,
      },
      {
        id: 'events',
        label: 'Events',
        labelKey: 'tasks.tabEvents',
        icon: Activity,
        panel: TASKS_EVENTS,
      },
    ],
  },
  {
    module: 'notifications',
    id: 'notifications',
    nodeGroup: 'host',
    title: 'Oznámení',
    titleKey: 'notifications.pageTitle',
    description: 'Historie oznámení a žádostí — co přišlo, kdy, v jakém stavu',
    descriptionKey: 'notifications.pageDesc',
    icon: Bell,
    showOnNode: false,
    tabs: [
      {
        id: 'history',
        label: 'Historie',
        labelKey: 'notifications.tabHistory',
        icon: Bell,
        panel: NOTIFICATIONS_HISTORY,
      },
    ],
  },
  {
    module: 'rustdesk',
    id: 'rustdesk',
    nodeGroup: 'workloads',
    title: 'RustDesk',
    titleKey: 'rustdesk.pageTitle',
    description: 'Vzdálená plocha — stanice, adresa, server',
    descriptionKey: 'rustdesk.subtitle',
    icon: Monitor,
    showOnNode: true,
    tabs: [
      {
        id: 'stations',
        label: 'Stanice',
        labelKey: 'rustdesk.tabStations',
        icon: Monitor,
        panel: RUSTDESK_STATIONS,
      },
      {
        id: 'books',
        label: 'Adresáře',
        labelKey: 'rustdesk.tabBooks',
        icon: BookUser,
        panel: RUSTDESK_BOOKS,
      },
      {
        id: 'audit',
        label: 'Audit',
        labelKey: 'rustdesk.tabAudit',
        icon: ScrollText,
        panel: RUSTDESK_AUDIT,
      },
      {
        id: 'packages',
        label: 'Balíčky',
        labelKey: 'rustdesk.tabPackages',
        icon: Package,
        panel: RUSTDESK_PACKAGES,
      },
      {
        id: 'server',
        label: 'Server',
        labelKey: 'rustdesk.tabServer',
        icon: Server,
        panel: RUSTDESK_SERVER,
      },
      {
        id: 'containers',
        label: 'Kontejnery',
        labelKey: 'rustdesk.tabContainers',
        icon: Box,
        panel: RUSTDESK_CONTAINERS,
      },
    ],
  },
];

/** Lookup pro modulovou stránku (podle module name). */
export function getModuleUi(moduleName: string): ModuleUiDef | undefined {
  return MODULE_UI_CATALOG.find((m) => m.module === moduleName);
}

/**
 * Jako `getModuleUi`, ale chybějící záznam je chyba konfigurace katalogu, ne
 * stav k ošetření. Stránky to dřív řešily `const DEF = getModuleUi(x);
 * if (!DEF) throw …` — jenže zúžení typu se do těla komponenty nepropíše,
 * takže `def={DEF}` pořád vidělo `ModuleUiDef | undefined`.
 */
export function requireModuleUi(moduleName: string): ModuleUiDef {
  const def = getModuleUi(moduleName);
  if (!def) throw new Error(`module-ui-catalog: ${moduleName} missing`);
  return def;
}

/** Lookup podle stabilního id (core stránky s module: null, např. addonstore). */
export function getModuleUiById(id: string): ModuleUiDef | undefined {
  return MODULE_UI_CATALOG.find((m) => m.id === id);
}

/** Pořadí skupin v Node workspace sidebaru. */
export const NODE_WORKSPACE_GROUPS: readonly {
  id: NodeWorkspaceGroup;
  label: string;
  labelKey: string;
}[] = [
  { id: 'host', label: 'Host', labelKey: 'infra.groupHost' },
  { id: 'workloads', label: 'Workloads', labelKey: 'infra.groupWorkloads' },
  { id: 'data', label: 'Data', labelKey: 'infra.groupData' },
  { id: 'platform', label: 'Platform', labelKey: 'infra.groupPlatform' },
] as const;

export interface NodeModuleGroup {
  def: ModuleUiDef;
  /** Záložky modulu (všechny z katalogu — filtr jen na úrovni modulu). */
  tabs: ModuleUiTab[];
  /** Klíč pro URL: def.id */
  groupKey: string;
}

/**
 * Moduly pro Node workspace: always-on + nainstalované, seskupené po modulech
 * (ne flat 50 záložek). `installed` = null → jen always-on (module: null).
 */
export function resolveNodeModuleGroups(
  installed: Set<string> | null,
): NodeModuleGroup[] {
  const out: NodeModuleGroup[] = [];
  for (const def of MODULE_UI_CATALOG) {
    if (def.showOnNode === false) continue;
    if (def.module !== null) {
      if (!installed || !installed.has(def.module)) continue;
    }
    if (!def.tabs.length) continue;
    out.push({ def, tabs: def.tabs, groupKey: def.id });
  }
  return out;
}

/**
 * Flat seznam záložek (zpětná kompatibilita / lookup tabKey).
 * Preferuj resolveNodeModuleGroups pro UI.
 */
export function resolveNodeModuleTabs(
  installed: Set<string> | null,
): Array<{ def: ModuleUiDef; tab: ModuleUiTab; tabKey: string }> {
  const out: Array<{ def: ModuleUiDef; tab: ModuleUiTab; tabKey: string }> = [];
  for (const g of resolveNodeModuleGroups(installed)) {
    for (const tab of g.tabs) {
      out.push({ def: g.def, tab, tabKey: `${g.groupKey}__${tab.id}` });
    }
  }
  return out;
}

/** tabKey = `${def.id}__${tab.id}` */
export function makeNodeTabKey(groupKey: string, tabId: string): string {
  return `${groupKey}__${tabId}`;
}

export function parseNodeTabKey(
  tabKey: string,
): { groupKey: string; tabId: string } | null {
  if (!tabKey || tabKey === 'overview') return null;
  const i = tabKey.indexOf('__');
  if (i <= 0) return null;
  return { groupKey: tabKey.slice(0, i), tabId: tabKey.slice(i + 2) };
}
